import { useMemo } from "react";
import uuid from "react-uuid";
import { ConfirmableActionFragment } from "../src/generated/graphql";

const STORED_CONFIRMABLE_ACTIONS_KEY: string = "stored-confirmable-actions";
const STORED_CONFIRMABLE_ACTIONS_MAX_SIZE: number = 3;

export interface StoredConfirmableAction {
  id: string;
  actionToken: string;
}

const instanceOfStoredConfirmableAction = (
  object: any
): object is StoredConfirmableAction => {
  return (
    typeof object.id === "string" && typeof object.actionToken === "string"
  );
};

const retrieveStoredConfirmableActionArray = (): any[] => {
  const storedConfirmableActionsJson: string =
    window.localStorage.getItem(STORED_CONFIRMABLE_ACTIONS_KEY) ?? "[]";
  let storedConfirmableActions: any;
  try {
    storedConfirmableActions = JSON.parse(storedConfirmableActionsJson);
  } catch (e) {
    // something went wrong, reset stored confirmable actions
    window.localStorage.setItem(STORED_CONFIRMABLE_ACTIONS_KEY, "[]");
    return [];
  }

  if (!Array.isArray(storedConfirmableActions)) {
    // something went wrong, reset stored confirmable actions
    window.localStorage.setItem(STORED_CONFIRMABLE_ACTIONS_KEY, "[]");
    return [];
  }

  return storedConfirmableActions;
};

export const retrieveConfirmableAction = (
  confirmableActionId: string
): StoredConfirmableAction | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const storedConfirmableActions: any[] =
    retrieveStoredConfirmableActionArray();
  const targetStoredConfirmableAction: any | undefined =
    storedConfirmableActions.find((action) => {
      return action.id === confirmableActionId;
    });

  if (targetStoredConfirmableAction === undefined) {
    return null;
  }

  if (!instanceOfStoredConfirmableAction(targetStoredConfirmableAction)) {
    // something went wrong, reset stored confirmable actions
    window.localStorage.setItem(STORED_CONFIRMABLE_ACTIONS_KEY, "[]");
    return null;
  }

  return targetStoredConfirmableAction;
};

export const storeConfirmableAction = (
  confirmableAction: ConfirmableActionFragment
) => {
  if (typeof window === "undefined") {
    return undefined;
  }

  let storedConfirmableActions: any[] = retrieveStoredConfirmableActionArray();
  if (
    storedConfirmableActions.some(
      (action) => action.id === confirmableAction.id
    )
  ) {
    return;
  }

  storedConfirmableActions.unshift({
    id: confirmableAction.id ?? "",
    actionToken: confirmableAction.actionToken,
  } as StoredConfirmableAction);
  storedConfirmableActions = storedConfirmableActions.splice(
    0,
    STORED_CONFIRMABLE_ACTIONS_MAX_SIZE
  );

  window.localStorage.setItem(
    STORED_CONFIRMABLE_ACTIONS_KEY,
    JSON.stringify(storedConfirmableActions)
  );
};
